import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FilterItem from '../../FilterItem';
import { clearSearch } from '../../../search/searchSlice';
import RecentSearches from './RecentSearches';
import { deepEqual } from '../../../../utilities/helpers';
import translate from '../../../../utilities/translator';
import SearchList from './SearchList';
import SearchInput from './SearchInput';
import useGetWhereLabel from './useGetWhereLabel';
import ChipsList from './ChipsList';
import OptionsList from './OptionsList';
import useHandleMainFilterState from '../../../../hooks/mainFilter/useHandleMainFilterState';
import { selectFilterData } from '../../selectors';
import { filterClearClick, filterSaveClick } from '../../../../appSlice';
import { getParamFromUrl, removeParamFromUrl } from '../../../../utilities/urlResolver';
import { clearLocationDeals } from '@/features/locationDeals/locationDealsSlice';

export default function Destination(props) {
  const {
    isLoading,
    onSave,
    onClear,
    onClose,
    showHistory = true,
    destinations,
    setDestinations,
    destinationsGroups,
    setDestinationsGroups,
    addFilterToSet,
  } = props;
  const dispatch = useDispatch();
  const popupRef = useRef();
  const clearTimerRef = useRef(null);
  const saveTimerRef = useRef(null);

  const mainFilter = useSelector(selectFilterData);
  const { where } = mainFilter.searchEngine;
  const [isClean, setIsClean] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const showClearButton =
    where.destinations.some((item) => item.active) ||
    where.destinationsGroups.some((item) => item.active);

  const label = useGetWhereLabel();

  useEffect(() => {
    return () => {
      clearTimeout(clearTimerRef.current);
      clearTimeout(saveTimerRef.current);
    }
  }, [])

  useEffect(() => {
    setIsClean(deepEqual(destinations, where.destinations)
      && deepEqual(destinationsGroups, where.destinationsGroups));
  }, [destinations, destinationsGroups, where]);

  const [isOpen, setIsOpen] = useHandleMainFilterState({ filter: 'where' });

  // Updates the state of a single checkbox
const updateCheckboxState = (id, array) => {
  return array.map((item) => {
    if (item.id === id) {
      return {
        ...item,
        active: !item.active,
      };
    }

    return item;
  });
};

  // Updates the group status based on the destinations
  const updateGroupStatus = (data) => {
    return destinationsGroups.map((group) => {
      if (!group.active) return group;

      const isUncheck = data
        .filter(
          (destination) =>
            Array.isArray(destination.destinations_groups) &&
            destination.destinations_groups.some((id) => id === group.id)
        )
        .some(({ active }) => !active);

      if (isUncheck) {
        return { ...group, active: false };
      }
      return group;
    });
  };

  // Updates the destinations status based on the group
  const updateDestinationStatus = (data) => {
    return destinations.map((destination) => {
      if (!Array.isArray(destination.destinations_groups)) return destination;

      const isActiveInDestinationGroup = data
        .filter(({ active }) => active)
        .some((group) =>
          destination.destinations_groups.some((id) => id === group.id)
        );

      return {
        ...destination,
        active: isActiveInDestinationGroup,
      };
    });
  };

  // Main function handling the checkbox change
  const handleCheckboxChange = (id, array, updateData, group) => {
    const data = updateCheckboxState(id, array);
    updateData(data);

    if (!group) {
      const updatedGroups = updateGroupStatus(data);
      setDestinationsGroups(updatedGroups);
    }

    if (group) {
      const updatedDestinations = updateDestinationStatus(data);
      setDestinations(updatedDestinations);
    }
  };

  const handleSaveClick = () => {
    setSearchValue('');
    dispatch(filterSaveClick());
    if (isClean) return undefined;

    dispatch(clearLocationDeals());

    saveTimerRef.current = setTimeout(() => {
      onSave({ destinations, destinationsGroups });
      if(getParamFromUrl('mainDestinationId')) {
        removeParamFromUrl(['mainDestinationId']);
      }
    }, 0);
    setIsOpen(false);
  };

  const handleClearClick = () => {
    setSearchValue('');
    if (
      isClean &&
      destinations.filter(({ active }) => active).length === 0
    ) {
      return undefined;
    }

    dispatch(filterClearClick());

    const updatedValue = {
      destinations: where.destinations.map((item) => ({
        ...item,
        active: false,
      })),
      destinationsGroups: where.destinationsGroups.map((item) => ({
        ...item,
        active: false,
      })),
    };

    clearTimerRef.current = setTimeout(() => {
      onClear(updatedValue);
      dispatch(clearSearch());
    }, 0);
  };

  window.external_reset_destinations = () => {
    addFilterToSet(() => {
      handleClearClick();
    });
  };

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleOutsideClick = () => {
    setDestinations(where.destinations);
    setDestinationsGroups(where.destinationsGroups);
  };

  const handleChipClick = (id) => handleCheckboxChange(id, destinations, setDestinations)

  const handleClosePopup = useCallback(() => {
    popupRef.current?.onClose()
  }, [popupRef])

  const isSearch = searchValue.length > 0;

  return (
    <FilterItem
      ref={popupRef}
      label={translate('holidayFinder.mainFilter.where')}
      placeholder={translate('holidayFinder.mainFilter.everywhere')}
      value={label}
      eventName="where"
      onSave={handleSaveClick}
      onClear={handleClearClick}
      onClose={onClose}
      icon="ri-map-pin-2-line"
      showClearButton={showClearButton}
      isLoading={isLoading}
      additionalClass={'destination'}
      clearLabel={translate('holidayFinder.buttons.Clear_all')}
      resetOnClickOutside={handleOutsideClick}
      clean={isClean}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div className="destination-filter">
        <div className="main-filter__item-wrap destination-filter__results">
          <div className="destination-filter__search">
            <SearchInput
              placeholder="holidayFinder.destinations.searchPlaceholder"
              onChange={handleSearchChange}
              value={searchValue}
            />
          </div>
          {isSearch ? (
            <SearchList
              searchValue={searchValue}
              destinations={destinations}
              destinationsGroups={destinationsGroups}
              setSearchValue={setSearchValue}
              setDestinationsGroups={setDestinationsGroups}
              setDestinations={setDestinations}
              handleCheckboxChange={handleCheckboxChange}
            />
          ) : (
            <>
              <div className="destination-filter__content">
                <ChipsList
                  destinations={destinations}
                  onClick={handleChipClick}
                />

                <OptionsList
                  list={destinationsGroups}
                  title={translate('holidayFinder.destinations.themes')}
                  onChange={(id) =>
                    handleCheckboxChange(
                      id,
                      destinationsGroups,
                      setDestinationsGroups,
                      true
                    )
                  }
                />

                <OptionsList
                  list={destinations}
                  title={translate('holidayFinder.destinations.favorite')}
                  onChange={(id) =>
                    handleCheckboxChange(id, destinations, setDestinations)
                  }
                />
              </div>

              {showHistory && (
                <div className="history__wrap__mobile">
                  <RecentSearches onApply={handleClosePopup} />
                </div>
              )}
            </>
          )}
        </div>

        {showHistory && (
          <div className="history__wrap__desktop">
            <RecentSearches onApply={handleClosePopup} />
          </div>
        )}
      </div>
    </FilterItem>
  );
}

Destination.propTypes = {
  onSave: PropTypes.func,
  onClear: PropTypes.func,
  showHistory: PropTypes.bool,
};
